import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { TripService } from '@/modules/trips'
import { useTrips as useTripsFromStore, useCurrentTrip, useTripActions, useTripStore, useTripLoading, useTripError } from '@/stores'
import { useAuth } from '@/modules/auth'
import {
  CreateTripDto,
  CreateQuickTripDto,
  UpdateTripDto,
  CreateWaypointDto,
  UpdateWaypointDto,
  AddMemberDto,
  UpdateMemberRoleDto,
  TripQueryDto
} from '@/shared/api'

// Module-level fetch guards shared across all useTrips() instances.
// Using refs inside the hook causes each component instance to have its own
// independent state, leading to duplicate concurrent fetches when multiple
// components (e.g. TripsSidebar + TripsSidebarContent) mount simultaneously.
let _hasFetched = false
let _isFetching = false
let _lastFetchTime = 0
const CACHE_DURATION_MS = 30000 // 30 seconds

export function resetTripFetchState() {
  _hasFetched = false
  _isFetching = false
  _lastFetchTime = 0
  useTripStore.getState().setHasFetched(false)
}

export function useTrips() {
  const navigate = useNavigate()
  const location = useLocation()
  const { getAccessToken, isAuthenticated } = useAuth()
  const trips = useTripsFromStore()
  const isLoading = useTripLoading()
  const error = useTripError()
  
  // Use individual selectors but memoize them to avoid recreation
  const setTrips = useTripStore(state => state.setTrips)
  const setPagination = useTripStore(state => state.setPagination)
  const setLoading = useTripStore(state => state.setLoading)
  const setError = useTripStore(state => state.setError)
  const clearError = useTripStore(state => state.clearError)
  const addTrip = useTripStore(state => state.addTrip)
  const updateTrip = useTripStore(state => state.updateTrip)
  const removeTrip = useTripStore(state => state.removeTrip)
  
  // Memoize actions object to prevent recreation
  const actions = useMemo(() => ({
    setTrips,
    setPagination,
    setLoading,
    setError,
    clearError,
    addTrip,
    updateTrip,
    removeTrip
  }), [setTrips, setPagination, setLoading, setError, clearError, addTrip, updateTrip, removeTrip])

  // Create trip service instance
  const tripService = useMemo(() => new TripService(getAccessToken), [getAccessToken])
  
  // Load trips
  const loadTrips = useCallback(
    async (
      query?: TripQueryDto & {
        includeArchived?: boolean
        archivedOnly?: boolean
        sort_by?: 'start_date' | 'end_date' | 'updated_at' | 'created_at' | 'name'
        sort_order?: 'asc' | 'desc'
      },
      forceRefresh: boolean = false
    ) => {
    // Prevent duplicate simultaneous fetches across all hook instances
    if (_isFetching && !forceRefresh) {
      return
    }
    
    // Check cache if not forcing refresh
    if (!forceRefresh) {
      const timeSinceLastFetch = Date.now() - _lastFetchTime
      if (_hasFetched && timeSinceLastFetch < CACHE_DURATION_MS) {
        return
      }
    }
    
    _isFetching = true
    _lastFetchTime = Date.now()
    useTripStore.getState().setLoading(true)
    useTripStore.getState().clearError()
    
    try {
      const response = await tripService.getTrips(query)
      useTripStore.getState().setTrips(response.data)
      useTripStore.getState().setPagination(response.pagination)
      useTripStore.getState().setHasFetched(true)
      _hasFetched = true
      return response
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load trips'
      useTripStore.getState().setError(errorMessage)
      throw error
    } finally {
      useTripStore.getState().setLoading(false)
      _isFetching = false
    }
  }, [tripService])

  // No auto-fetch here — consumers (e.g. TripsSidebarContent) own the fetch
  // and pass the correct query params (e.g. includeArchived) for their context.

  // Create trip
  const createTrip = useCallback(async (tripData: CreateTripDto) => {
    actions.setLoading(true)
    actions.clearError()
    
    try {
      const newTrip = await tripService.createTrip(tripData)
      actions.addTrip(newTrip)
      navigate(`/trips/${newTrip.id}`)
      return newTrip
    } catch (error: any) {
      // Extract error message from different error formats
      let errorMessage = 'Failed to create trip'
      
      if (error instanceof Error) {
        errorMessage = error.message
      } else if (error?.response?.data?.error?.message) {
        // Backend error format: { success: false, error: { message: "..." } }
        errorMessage = error.response.data.error.message
      } else if (error?.response?.data?.error) {
        // Backend error format: { success: false, error: "..." }
        errorMessage = typeof error.response.data.error === 'string' 
          ? error.response.data.error 
          : error.response.data.error.message || 'Failed to create trip'
      } else if (error?.message) {
        errorMessage = error.message
      } else if (typeof error === 'string') {
        errorMessage = error
      }
      
      // Prevent "[object Object]" from being displayed
      if (errorMessage === '[object Object]' || (typeof errorMessage === 'object' && errorMessage !== null)) {
        errorMessage = 'Failed to create trip. Please try again.'
      }
      
      actions.setError(errorMessage)
      throw error
    } finally {
      actions.setLoading(false)
    }
  }, [tripService, actions, navigate])

  // Create quick spontaneous trip
  const createQuickTrip = useCallback(async (quickTripData: CreateQuickTripDto) => {
    actions.setLoading(true)
    actions.clearError()
    
    try {
      const newTrip = await tripService.createQuickSpontaneousTrip(quickTripData)
      actions.addTrip(newTrip)
      navigate(`/trips/${newTrip.id}`)
      return newTrip
    } catch (error: any) {
      // Extract error message from different error formats
      let errorMessage = 'Failed to create quick trip'
      
      if (error instanceof Error) {
        errorMessage = error.message
      } else if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message
      } else if (error?.response?.data?.error) {
        errorMessage = typeof error.response.data.error === 'string' 
          ? error.response.data.error 
          : error.response.data.error.message || 'Failed to create quick trip'
      } else if (error?.message) {
        errorMessage = error.message
      } else if (typeof error === 'string') {
        errorMessage = error
      }
      
      if (errorMessage === '[object Object]' || (typeof errorMessage === 'object' && errorMessage !== null)) {
        errorMessage = 'Failed to create quick trip. Please try again.'
      }
      
      actions.setError(errorMessage)
      throw error
    } finally {
      actions.setLoading(false)
    }
  }, [tripService, actions, navigate])

  // Update trip
  const updateTripData = useCallback(async (tripId: string, tripData: UpdateTripDto) => {
    actions.setLoading(true)
    actions.clearError()
    
    try {
      const updatedTrip = await tripService.updateTrip(tripId, tripData)
      actions.updateTrip(tripId, updatedTrip)
      return updatedTrip
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update trip'
      actions.setError(errorMessage)
      throw error
    } finally {
      actions.setLoading(false)
    }
  }, [tripService, actions])

  // Delete trip
  const deleteTripData = useCallback(async (tripId: string) => {
    actions.setLoading(true)
    actions.clearError()
    
    try {
      await tripService.deleteTrip(tripId)
      actions.removeTrip(tripId)
      
      // Clear localStorage cache for deleted trip
      const cacheKey = `trip_${tripId}`
      localStorage.removeItem(cacheKey)
      
      // Reload trips list to ensure UI is in sync
      await loadTrips()
      
      // Only navigate to trips list if we're not already there
      if (location.pathname !== '/trips') {
        navigate('/trips')
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete trip'
      actions.setError(errorMessage)
      throw error
    } finally {
      actions.setLoading(false)
    }
  }, [tripService, actions, navigate, loadTrips, location.pathname])

  // Waypoint operations
  const addWaypoint = useCallback(async (tripId: string, waypointData: CreateWaypointDto) => {
    try {
      return await tripService.addWaypoint(tripId, waypointData)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add waypoint'
      actions.setError(errorMessage)
      throw error
    }
  }, [tripService, actions])

  const updateWaypointData = useCallback(async (
    tripId: string,
    waypointId: string,
    waypointData: UpdateWaypointDto
  ) => {
    try {
      return await tripService.updateWaypoint(tripId, waypointId, waypointData)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update waypoint'
      actions.setError(errorMessage)
      throw error
    }
  }, [tripService, actions])

  const deleteWaypointData = useCallback(async (tripId: string, waypointId: string) => {
    try {
      await tripService.deleteWaypoint(tripId, waypointId)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete waypoint'
      actions.setError(errorMessage)
      throw error
    }
  }, [tripService, actions])

  const reorderWaypoints = useCallback(async (tripId: string, waypointIds: string[]) => {
    try {
      await tripService.reorderWaypoints(tripId, waypointIds)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reorder waypoints'
      actions.setError(errorMessage)
      throw error
    }
  }, [tripService, actions])

  // Member operations
  const addMember = useCallback(async (tripId: string, memberData: AddMemberDto) => {
    try {
      return await tripService.addMember(tripId, memberData)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add member'
      actions.setError(errorMessage)
      throw error
    }
  }, [tripService, actions])

  const updateMemberRole = useCallback(async (
    tripId: string,
    memberId: string,
    roleData: UpdateMemberRoleDto
  ) => {
    try {
      return await tripService.updateMemberRole(tripId, memberId, roleData)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update member role'
      actions.setError(errorMessage)
      throw error
    }
  }, [tripService, actions])

  const removeMember = useCallback(async (tripId: string, memberId: string) => {
    try {
      await tripService.removeMember(tripId, memberId)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to remove member'
      actions.setError(errorMessage)
      throw error
    }
  }, [tripService, actions])

  const leaveTrip = useCallback(async (tripId: string) => {
    try {
      await tripService.leaveTrip(tripId)
      navigate('/dashboard')
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to leave trip'
      actions.setError(errorMessage)
      throw error
    }
  }, [tripService, actions, navigate])

  // Search and public trips
  const searchTrips = useCallback(async (query: string, filters?: Omit<TripQueryDto, 'search'>) => {
    try {
      return await tripService.searchTrips(query, filters)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to search trips'
      actions.setError(errorMessage)
      throw error
    }
  }, [tripService, actions])

  const getPublicTrips = useCallback(async (query?: Omit<TripQueryDto, 'is_public'>) => {
    try {
      return await tripService.getPublicTrips(query)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load public trips'
      actions.setError(errorMessage)
      throw error
    }
  }, [tripService, actions])

  // Archive operations
  const archiveTrip = useCallback(async (tripId: string) => {
    actions.setLoading(true)
    actions.clearError()
    
    try {
      await tripService.archiveTrip(tripId)
      // Update trip in store to mark as archived
      const trip = trips.find(t => t.id === tripId)
      if (trip) {
        actions.updateTrip(tripId, { ...trip, archived_at: new Date().toISOString() } as any)
      }
      // Reload trips to refresh the list
      await loadTrips(undefined, true)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to archive trip'
      actions.setError(errorMessage)
      throw error
    } finally {
      actions.setLoading(false)
    }
  }, [tripService, actions, trips, loadTrips])

  const unarchiveTrip = useCallback(async (tripId: string) => {
    actions.setLoading(true)
    actions.clearError()
    
    try {
      await tripService.unarchiveTrip(tripId)
      // Update trip in store to mark as unarchived
      const trip = trips.find(t => t.id === tripId)
      if (trip) {
        actions.updateTrip(tripId, { ...trip, archived_at: null } as any)
      }
      // Reload trips to refresh the list
      await loadTrips(undefined, true)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to unarchive trip'
      actions.setError(errorMessage)
      throw error
    } finally {
      actions.setLoading(false)
    }
  }, [tripService, actions, trips, loadTrips])

  const restoreTripFromRetention = useCallback(async (tripId: string) => {
    actions.setLoading(true)
    actions.clearError()
    try {
      const updated = await tripService.restoreTripFromRetention(tripId)
      const existing = trips.find((t) => t.id === tripId)
      if (existing) {
        actions.updateTrip(tripId, { ...existing, ...updated } as any)
      }
      await loadTrips(undefined, true)
      return updated
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to restore trip'
      actions.setError(errorMessage)
      throw error
    } finally {
      actions.setLoading(false)
    }
  }, [tripService, actions, trips, loadTrips])

  return {
    // State
    trips,
    isLoading,
    error,
    
    // Actions
    loadTrips,
    createTrip,
    createQuickTrip,
    updateTrip: updateTripData,
    deleteTrip: deleteTripData,
    
    // Waypoint operations
    addWaypoint,
    updateWaypoint: updateWaypointData,
    deleteWaypoint: deleteWaypointData,
    reorderWaypoints,
    
    // Member operations
    addMember,
    updateMemberRole,
    removeMember,
    leaveTrip,
    
    // Search and public trips
    searchTrips,
    getPublicTrips,
    
    // Archive operations
    archiveTrip,
    unarchiveTrip,
    restoreTripFromRetention,
    
    // Utility
    clearError
  }
}

// Hook for managing a single trip
export function useTrip(tripId: string) {
  const { getAccessToken, isAuthenticated } = useAuth()
  const currentTrip = useCurrentTrip()
  const isLoading = useTripStore(state => state.isLoading)
  const error = useTripStore(state => state.error)
  
  // Use individual selectors but memoize them to avoid recreation
  const setCurrentTrip = useTripStore(state => state.setCurrentTrip)
  const setLoading = useTripStore(state => state.setLoading)
  const setError = useTripStore(state => state.setError)
  const clearError = useTripStore(state => state.clearError)
  
  // Memoize actions object to prevent recreation
  const actions = useMemo(() => ({
    setCurrentTrip,
    setLoading,
    setError,
    clearError
  }), [setCurrentTrip, setLoading, setError, clearError])

  // Create trip service instance
  const tripService = useMemo(() => new TripService(getAccessToken), [getAccessToken])

  // Load trip details
  const loadTrip = useCallback(async () => {
    if (!tripId) return null
    
    useTripStore.getState().setLoading(true)
    useTripStore.getState().clearError()
    
    try {
      const trip = await tripService.getTrip(tripId)
      useTripStore.getState().setCurrentTrip(trip)
      return trip
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load trip'
      useTripStore.getState().setError(errorMessage)
      throw error
    } finally {
      useTripStore.getState().setLoading(false)
    }
  }, [tripId, tripService])

  // Load trip on mount (only when authenticated)
  useEffect(() => {
    if (isAuthenticated && tripId) {
      loadTrip()
    }
  }, [isAuthenticated, tripId]) // Removed loadTrip from dependencies to prevent infinite loop

  return {
    trip: currentTrip,
    isLoading,
    error,
    loadTrip,
    clearError
  }
}
